import { For, Match, Show, Switch, createContext, createEffect, createMemo, createSignal, untrack, useContext } from "solid-js";
import { FilterEditor } from "./FilterEditor";
import { UserFilterGroupInstance } from './UserFilterGroupInstance';
import { userFilters } from "./UserFilterStore";
import Button from "../st-button/st-button";
import STToolButton from "../st-tool-button/st-tool-button";
import STModal from "../st-modal/st-modal";
import STCheckbox from '../st-checkbox/st-checkbox';
import { viewFilters as api } from 'st-app/api';
import './st-view-filters.css';
import Dropdown from "../st-dropdown/st-dropdown";

class STViewFilterPropEvents {
    openEditor?: () => void;
    refresh?: () => void;
    onSelectionChanged?: () => void;
}

class STViewFilterProps {
    primaryObjectGU: string = '';
    events = new STViewFilterPropEvents();
    showDropdown = false;
    showFilterEditor = false;

    getSharedFilters() {
        return userFilters.sharedFilters.filter(f => f.primaryGU === this.primaryObjectGU);
    }

    getFilters() {
        return userFilters.allFilters.filter(f => f.primaryGU === this.primaryObjectGU);
    }
}

const ViewFiltersContext = createContext(new STViewFilterProps());
export const useViewFiltersContext = () => useContext(ViewFiltersContext);

export default function STViewFilter(props: STViewFilterProps) {
    const ctx = createMemo(() => {
        const res = new STViewFilterProps();
        res.primaryObjectGU = props.primaryObjectGU;

        return res;
    });
    const sharedFilters = createMemo(() => ctx().getSharedFilters());
    const filters = createMemo(() => ctx().getFilters());
    const checklists = createMemo(() => userFilters.allChecklists.filter(cl=>cl.objectID === ctx().primaryObjectGU));

    const groups = createMemo(() => userFilters.allGroups.map(g => g).sort((a, b) => a.order - b.order));
    const [showFilterEditor, setShowFilterEditor] = createSignal(props.showFilterEditor);
    const [showDropdown, setShowDropdown] = createSignal(props.showDropdown || props.showFilterEditor);

    const userCollectionGU = createMemo(() => {
        const clPrefs = userFilters.checklistPrefs;
        const pref = clPrefs[ctx().primaryObjectGU];

        return pref?.userFilterCollectionGU ?? '';
    });

    const anyEnabled = createMemo(() => {
        const selectedFilters = filters().filter(f => f.isEnabled[0]());
        const clSelected = !!userCollectionGU();

        return selectedFilters.length > 0 || clSelected;
    });

    const showButton = createMemo(() => filters().length > 0 || checklists().length > 0 || sharedFilters().length > 0);

    createEffect(() => setShowFilterEditor(props.showFilterEditor));

    createEffect(() => {
        const selectedFilters = filters().filter(f => f.isEnabled[0]());
        const clSelected = !!userCollectionGU();
        const trackChanges = showButton();

        const showDD = untrack(showDropdown);
        const showFE = untrack(showFilterEditor);

        if (showDD || showFE) {
            if (props.events.onSelectionChanged && trackChanges) {
                requestAnimationFrame(() => {
                    props.events.onSelectionChanged!();
                });
            }
        }
    });

    props.events.openEditor = () => {
        setShowDropdown(true);
        setShowFilterEditor(true);
    };

    props.events.refresh = () => {
        userFilters.init();
    };

    const isDropdownOpen = createMemo(() => showDropdown() || showFilterEditor())

    return <ViewFiltersContext.Provider value={ctx()}>
        <Show when={showButton()}>
            <div id="REV_FILTERS" class="REV_FILTERS REV_MENU" classList={{ filtered: anyEnabled() }}>
                <Dropdown class="REV_FILTERS" open={[isDropdownOpen, setShowDropdown]}>
                    <Dropdown.Trigger as={Button} icon="fa fa-filter" type="default">
                        <span class="fa fa-check-circle filtered-check" role="presentation"></span>
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <div class="flexbox horizontal padded nowrap">
                            <div>
                                <div class="flexbox horizontal middle">
                                    <h3 class="auto">User Filters</h3>
                                    <div>
                                        <STToolButton icon="fa fa-edit" label="Edit Filters" onClick={() => setShowFilterEditor(true)}>
                                            <Show when={sharedFilters().length > 0}>
                                                <STBadge value={sharedFilters().length} />
                                            </Show>
                                        </STToolButton>
                                    </div>
                                </div>
                                <div style="overflow-y: auto; overflow-x: hidden; max-height: 480px">
                                    <For each={groups()}>{(group) =>
                                        <FilterGroup group={group} />
                                    }</For>
                                    <FilterGroup group={undefined} />
                                </div>
                            </div>
                            <Show when={checklists().length > 0}>
                                <div class="filter-checklists" style="border-left: 1px solid var(--st-color-border); white-space: nowrap">
                                    <h3>Checklist Filters</h3>
                                    <div class="layout-table middle padded" style="overflow: auto">
                                        <label class="layout-row" classList={{ checked: !userCollectionGU() }}>
                                            <span class="layout-column">
                                                <input type="radio"
                                                    name="filter-checklist"
                                                    checked={!userCollectionGU()}
                                                    onInput={() => userFilters.updateCollectionGU(ctx().primaryObjectGU, '')} />
                                            </span>
                                            <span class="layout-column">None</span>
                                        </label>
                                        <For each={checklists()}>{(list) =>
                                            <label class="layout-row" classList={{ checked: userCollectionGU() === list.collectionGU }}>
                                                <span class="layout-column">
                                                    <input type="radio"
                                                        name="filter-checklist"
                                                        value={list.collectionGU}
                                                        onInput={() => userFilters.updateCollectionGU(list.objectID || '', list.collectionGU || '')}
                                                        checked={userCollectionGU() === list.collectionGU} />
                                                </span>
                                                <span class="layout-column">{list.name}</span>
                                            </label>
                                        }</For>
                                    </div>
                                </div>
                            </Show>
                        </div>
                    </Dropdown.Content>
                </Dropdown>

                <STModal class="st-edit-filter-modal" show={[showFilterEditor, setShowFilterEditor]} title="Edit Filters">
                    <FilterEditor primaryObjectGU={props.primaryObjectGU} setShowDialog={setShowFilterEditor}></FilterEditor>
                </STModal>
            </div>
        </Show>
    </ViewFiltersContext.Provider>
}

STViewFilter.defaultProps = new STViewFilterProps();

function STBadge(props: { value: string | number }) {
    return <Show when={props.value}>
        <span class="st-badge">
            {props.value}
        </span>
    </Show>
}

function FilterGroup(props: { group: UserFilterGroupInstance | undefined }) {
    const ctx = useViewFiltersContext();
    const filters = createMemo(() => ctx.getFilters().filter(f => {
        return (!props.group && !f.filterGroupGU)
            || f.filterGroupGU === props.group?.filterGroupGU;
    }));

    const isExpanded = createMemo(() => userFilters.isGroupExpanded(props.group?.filterGroupGU || ''));

    return <Show when={filters().length > 0}>
        <div class="filter-group">
            <Show when={props.group}>
                <div class="flexbox horizontal middle padded" role="button" style="cursor: pointer" onClick={() => userFilters.toggleExpandedGroup(props.group!.filterGroupGU || '')}>
                    <Switch>
                        <Match when={isExpanded()}>
                            <span aria-label="Collapse">
                                <i class="fa fa-caret-down" role="presentation"></i>
                            </span>
                        </Match>
                        <Match when={!isExpanded()}>
                            <span aria-label="Expand">
                                <i class="fa fa-caret-right" role="presentation"></i>
                            </span>
                        </Match>
                    </Switch>
                    <h4>{props.group!.name[0]()}</h4>
                </div>
            </Show>
            <Show when={!props.group || isExpanded() }>
                <ul class="filter-list" classList={{ root: !props.group }}>
                    <For each={filters()}>{(filter) => {
                        let initialized = false;
                        createEffect(() => {
                            const enabled = filter.isEnabled[0]();
                            if (initialized) {
                                api.SetFilter(filter.filterGU!, enabled);
                            }
                            initialized = true;
                        });

                        return <li>
                            <STCheckbox label={filter.name$[0]()} value={filter.isEnabled} />
                        </li>
                    }}</For>
                </ul>
            </Show>
        </div>
    </Show>
}